import React, { useState, useEffect, useCallback } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import FilterBar from '../components/FilterBar';
import api from '../api';
import { useAuth } from '../AuthContext';

function fmt(amount, dec = 0) {
  if (!amount) return '';
  return '$' + parseFloat(amount).toLocaleString('en-US', { maximumFractionDigits: dec, minimumFractionDigits: dec });
}
function fmtDate(s, tz = 'America/Chicago') {
  if (!s) return '';
  return new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz });
}
function fmtTime(s, tz = 'America/Chicago') {
  if (!s) return '';
  return new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: tz });
}

function DetailPanel({ details }) {
  if (!details) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 20px', color: '#94A3B8', fontSize: '13px' }}>
      <i className="fas fa-spinner fa-spin"></i> Loading details...
    </div>
  );
  if (details.error) return (
    <div style={{ padding: '20px', color: '#DC2626', fontSize: '13px' }}>
      <i className="fas fa-exclamation-circle mr-2"></i>Could not load details.
    </div>
  );

  const d = details.data || {};
  const delivery = details.lead_delivery || [];

  const Field = ({ label, value }) => value ? (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: '#0F172A', fontWeight: '500' }}>{value}</div>
    </div>
  ) : null;

  return (
    <div style={{ background: '#F8FAFC', padding: '20px 24px', borderTop: '2px solid #2D87F0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>

        {/* Contact */}
        <div>
          <div style={{ fontWeight: '700', fontSize: '12px', color: '#2D87F0', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fas fa-user"></i> Contact
          </div>
          <Field label="Name" value={[d.first_name, d.last_name].filter(Boolean).join(' ')} />
          <Field label="Email" value={d.email} />
          <Field label="Phone" value={d.phone} />
          <Field label="Age" value={d.age} />
          <Field label="Gender" value={d.gender_name} />
          <Field label="State" value={d.state_code} />
        </div>

        {/* Quote */}
        <div>
          <div style={{ fontWeight: '700', fontSize: '12px', color: '#2D87F0', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fas fa-file-invoice-dollar"></i> Quote Details
          </div>
          <Field label="Engine" value={d.engine_name} />
          <Field label="Coverage" value={d.coverage_amount ? fmt(d.coverage_amount, 0) : ''} />
          <Field label="Coverage Type" value={d.coverage_term} />
          <Field label="Carrier" value={d.insurance_company} />
          <Field label="Product" value={d.insurance_plan} />
          <Field label="Monthly" value={d.monthly_premium ? fmt(d.monthly_premium) + '/mo' : ''} />
          <Field label="Annual" value={d.annual_premium ? fmt(d.annual_premium, 0) + '/yr' : ''} />
        </div>

        {/* Delivery */}
        <div>
          <div style={{ fontWeight: '700', fontSize: '12px', color: '#2D87F0', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fas fa-paper-plane"></i> Lead Delivery
          </div>
          {delivery.map((del, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px 10px', background: 'white', border: '1px solid #DDE7F1', borderRadius: '7px' }}>
              <i className={`fas ${del.response_type === 0 ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ color: del.response_type === 0 ? '#16A34A' : '#DC2626', fontSize: '14px' }}></i>
              <span style={{ fontSize: '12px', color: '#334155' }}>{del.title}</span>
            </div>
          ))}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {d.quote_url && (
              <a href={d.quote_url} target="_blank" rel="noreferrer" className="btn-secondary" style={{ fontSize: '12px', padding: '6px 12px', textDecoration: 'none', height: '30px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <i className="fas fa-external-link-alt"></i> View Results
              </a>
            )}
            <button className="btn-primary" style={{ fontSize: '12px', padding: '6px 12px', height: '30px' }}>View Full Report</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LeadsPage() {
  const { brand } = useAuth();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [term, setTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leadType, setLeadType] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const [alertsActive, setAlertsActive] = useState(false);

  const fetchLeads = useCallback(async (p = 1, filters = {}, ps = pageSize) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: ps };
      const t  = filters.term      ?? term;
      const s  = filters.start     ?? startDate;
      const e  = filters.end       ?? endDate;
      const lt = filters.leadType  ?? leadType;
      const mn = filters.minAmount ?? minAmount;
      const mx = filters.maxAmount ?? maxAmount;
      if (t)  params.term = t;
      if (s)  params.start = s;
      if (e)  params.end = e;
      if (lt) params.lead_type = lt;
      if (mn) params.min_amount = mn;
      if (mx) params.max_amount = mx;
      const res = await api.get('/leads', { params });
      setLeads(res.data.leads);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setPage(p);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [term, startDate, endDate, leadType, minAmount, maxAmount, pageSize]);

  useEffect(() => { fetchLeads(); }, []);

  const loadDetails = async (leadId) => {
    try {
      const res = await api.get(`/leads/${leadId}/details`);
      setDetailsCache(prev => ({ ...prev, [leadId]: res.data }));
    } catch { setDetailsCache(prev => ({ ...prev, [leadId]: { error: true } })); }
  };

  const handleToggle = (id) => {
    if (expandedId !== id && !detailsCache[id]) loadDetails(id);
    setExpandedId(prev => prev === id ? null : id);
  };

  const handleReset = () => {
    setTerm(''); setStartDate(''); setEndDate('');
    setLeadType(''); setMinAmount(''); setMaxAmount('');
    fetchLeads(1, { term: '', start: '', end: '', leadType: '', minAmount: '', maxAmount: '' });
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0F172A', margin: 0 }}>All Leads</h1>
          {/* Lead alerts toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#64748B' }}>Lead Alerts</span>
            <button data-testid="lead-alerts-toggle"
              onClick={() => setAlertsActive(!alertsActive)}
              style={{ width: '44px', height: '24px', borderRadius: '999px', background: alertsActive ? '#EFF6FF' : '#F1F5F9', border: `1px solid ${alertsActive ? '#2D87F0' : '#DDE7F1'}`, position: 'relative', cursor: 'pointer', padding: 0, overflow: 'hidden' }}>
              <span style={{ position: 'absolute', top: '50%', left: alertsActive ? 'calc(100% - 19px)' : '3px', transform: 'translateY(-50%)', width: '16px', height: '16px', borderRadius: '50%', background: alertsActive ? '#2D87F0' : '#94A3B8', transition: 'left 200ms, background 200ms' }} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <FilterBar
          startDate={startDate} endDate={endDate}
          onDateChange={(s, e) => { setStartDate(s); setEndDate(e); fetchLeads(1, { start: s, end: e }); }}
          contact={term} onContactChange={(v) => { setTerm(v); fetchLeads(1, { term: v }); }}
          typeOptions={[
            { value: 'New Lead', label: 'New Lead' },
            { value: 'Application Request', label: 'Application Request' },
            { value: 'Quote Started', label: 'Quote Started' },
          ]}
          selectedType={leadType} onTypeChange={(v) => { setLeadType(v); fetchLeads(1, { leadType: v }); }}
          typePlaceholder="All lead types"
          minAmount={minAmount} maxAmount={maxAmount}
          onAmountChange={(mn, mx) => { setMinAmount(mn); setMaxAmount(mx); fetchLeads(1, { minAmount: mn, maxAmount: mx }); }}
          onReset={handleReset}
        />

        {/* Results card */}
        <div className="portal-card" style={{ overflow: 'hidden' }}>

          {/* Table toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: '#64748B' }}>
              {loading ? 'Loading...' : total === 0 ? 'No leads found' : `Showing ${from}–${to} of ${total.toLocaleString()} leads`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Page size */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#64748B' }}>Per page:</span>
                <select value={pageSize} onChange={e => { const ps = parseInt(e.target.value); setPageSize(ps); fetchLeads(1, {}, ps); }}
                  style={{ height: '28px', padding: '0 8px', border: '1px solid #DDE7F1', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', appearance: 'none', paddingRight: '20px', background: 'white' }}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button onClick={() => fetchLeads(page - 1)} disabled={page === 1}
                    style={{ width: '28px', height: '28px', border: '1px solid #DDE7F1', borderRadius: '6px', background: 'white', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, fontSize: '12px' }}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let p;
                    if (totalPages <= 7) p = i + 1;
                    else if (page <= 4) p = i + 1;
                    else if (page >= totalPages - 3) p = totalPages - 6 + i;
                    else p = page - 3 + i;
                    return (
                      <button key={p} onClick={() => fetchLeads(p)}
                        style={{ width: '28px', height: '28px', border: `1px solid ${p === page ? '#2D87F0' : '#DDE7F1'}`, borderRadius: '6px', background: p === page ? '#2D87F0' : 'white', color: p === page ? 'white' : '#334155', cursor: 'pointer', fontSize: '12px', fontWeight: p === page ? '600' : '400' }}>
                        {p}
                      </button>
                    );
                  })}
                  <button onClick={() => fetchLeads(page + 1)} disabled={page === totalPages}
                    style={{ width: '28px', height: '28px', border: '1px solid #DDE7F1', borderRadius: '6px', background: 'white', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1, fontSize: '12px' }}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ padding: '20px' }}>
              {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: '52px', borderRadius: '6px', marginBottom: '8px' }}></div>)}
            </div>
          ) : leads.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8' }}>
              <i className="fas fa-inbox" style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}></i>
              No leads found
            </div>
          ) : (
            <table data-testid="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['Date', 'Type', 'Contact', 'Quote Info', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #DDE7F1', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  const isExpanded = expandedId === lead.id;
                  const details = detailsCache[lead.id];
                  const selected = lead.details?.quote?.selected_row;
                  const lowest  = lead.details?.quote?.lowest_row;
                  const qrow    = selected || lowest;
                  return (
                    <React.Fragment key={lead.id}>
                      <tr
                        onClick={() => handleToggle(lead.id)}
                        style={{ borderBottom: isExpanded ? 'none' : '1px solid #F1F5F9', cursor: 'pointer', background: isExpanded ? '#EFF6FF' : 'white', transition: 'background 150ms' }}
                        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#FAFBFF'; }}
                        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'white'; }}
                      >
                        {/* Date */}
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#0F172A' }}>{fmtDate(lead.created_at, brand?.timezone)}</div>
                          <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '1px' }}>{fmtTime(lead.created_at, brand?.timezone)}</div>
                        </td>
                        {/* Type */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontSize: '13px', color: '#334155' }}>{lead.lead_type}</div>
                          <div style={{ marginTop: '3px' }}>
                            {lead.partial ? (
                              <span style={{ fontSize: '11px', background: '#F1F5F9', color: '#64748B', borderRadius: '999px', padding: '1px 8px', border: '1px solid #DDE7F1' }}>Partial</span>
                            ) : lead.repeat ? (
                              <span style={{ fontSize: '11px', background: '#F1F5F9', color: '#64748B', borderRadius: '999px', padding: '1px 8px', border: '1px solid #DDE7F1' }}>Repeat</span>
                            ) : (
                              <span style={{ fontSize: '11px', background: '#EFF6FF', color: '#2D87F0', borderRadius: '999px', padding: '1px 8px', border: '1px solid #BFDBFE' }}>New</span>
                            )}
                          </div>
                        </td>
                        {/* Contact */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#0F172A' }}>{lead.details?.lead?.full_name || '—'}</div>
                          <div style={{ fontSize: '12px', color: '#64748B', marginTop: '1px' }}>{lead.details?.lead?.email}</div>
                        </td>
                        {/* Quote info */}
                        <td style={{ padding: '14px 16px' }}>
                          {qrow ? (
                            <>
                              <div style={{ fontSize: '13px', fontWeight: '500', color: '#0F172A' }}>
                                {selected ? fmt(selected.annual_premium, 0) + '/yr' : <span style={{ color: '#64748B' }}>Lowest: {fmt(lowest?.annual_premium, 0)}/yr</span>}
                              </div>
                              <div style={{ fontSize: '11px', color: '#64748B', marginTop: '1px' }}>{qrow.insurance_company}{qrow.insurance_plan ? ` – ${qrow.insurance_plan}` : ''}</div>
                            </>
                          ) : <span style={{ color: '#94A3B8', fontSize: '13px' }}>—</span>}
                        </td>
                        {/* Expand */}
                        <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                          <button data-testid="data-table-row-expand-button"
                            onClick={e => { e.stopPropagation(); handleToggle(lead.id); }}
                            style={{ width: '30px', height: '30px', borderRadius: '7px', border: `1px solid ${isExpanded ? '#2D87F0' : '#DDE7F1'}`, background: isExpanded ? '#2D87F0' : 'white', color: isExpanded ? 'white' : '#64748B', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', transition: 'all 150ms' }}>
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0 }} data-testid="data-table-row-expanded-panel">
                            <DetailPanel details={details} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </ProtectedLayout>
  );
}
